<template>
  <div class="amc-exercise">
    <el-card class="exercise-card">
      <div slot="header" class="card-header">
        <h2>AMC练习</h2>
      </div>

      <el-form :model="exerciseForm" :rules="rules" ref="exerciseForm" label-width="120px">
        <!-- 题库选择 -->
        <el-form-item label="题库" prop="repoId">
          <el-radio-group v-model="exerciseForm.repoId">
            <el-radio :label="9">AMC</el-radio>
            <el-radio :label="1">基础题库</el-radio>
            <el-radio :label="2">进阶题库</el-radio>
            <el-radio :label="3">竞赛题库</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 知识点选择 -->
        <el-form-item label="知识点" prop="knowledgeId">
          <el-select
            v-model="exerciseForm.knowledgeId"
            placeholder="请选择知识点"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="item in knowledgeOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <!-- 难度系数选择 -->
        <el-form-item label="难度系数" prop="difficulty">
          <el-radio-group v-model="exerciseForm.difficulty">
            <el-radio :label="1">简单</el-radio>
            <el-radio :label="2">中等</el-radio>
            <el-radio :label="3">困难</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 题目数量选择 -->
        <el-form-item label="题目数量" prop="questionCount">
          <el-input-number
            v-model="exerciseForm.questionCount"
            :min="1"
            :max="50"
            :step="1"
            style="width: 200px"
          ></el-input-number>
          <span class="tip-text">（建议选择5-20题）</span>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
          <el-button type="primary" @click="startExercise" :loading="loading">
            开始练习
          </el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 练习结果展示 -->
    <el-card v-if="exerciseQuestions.length > 0" class="questions-card">
      <div slot="header" class="card-header">
        <h3>练习题目</h3>
        <span class="question-count">共 {{ exerciseQuestions.length }} 题</span>
      </div>

      <div class="questions-container">
        <div
          v-for="(question, index) in exerciseQuestions"
          :key="question.id"
          class="question-item"
        >
          <div class="question-header">
            <span class="question-number">第 {{ index + 1 }} 题</span>
            <div class="question-meta">
              <span class="question-knowledge">{{ question.knowledge }}</span>
              <span class="question-difficulty" v-if="question.difficulty" :class="getDifficultyClass(question.difficulty)">
                {{ getDifficultyText(question.difficulty) }}
              </span>
              <span class="question-difficulty difficulty-unknown" v-else>
                未知难度
              </span>
            </div>
          </div>

          <div class="question-content" v-html="question.content"></div>

          <!-- 如果有选项则显示选项，否则显示答案输入框 -->
          <div class="question-options" v-if="question.options && question.options.length > 0">
            <el-radio-group v-model="userAnswers[index]">
              <el-radio
                v-for="option in question.options"
                :key="option.key"
                :label="option.key"
                class="option-item"
              >
                {{ option.key }}. {{ option.value }}
              </el-radio>
            </el-radio-group>
          </div>

          <!-- 如果没有选项，显示答案输入框 -->
          <div class="question-answer-input" v-else>
            <el-input
              v-model="userAnswers[index]"
              placeholder="请输入答案"
              maxlength="10"
              show-word-limit
              style="width: 200px"
            ></el-input>
            <span class="answer-tip">（请输入答案，如：A、B、C、D、E 或具体数值）</span>
          </div>
        </div>

        <div class="submit-section">
          <el-button type="success" @click="submitAnswers" :loading="submitting">
            提交答案
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 答题结果展示 -->
    <el-card v-if="showResults && questionResults.length > 0" class="results-card">
      <div slot="header" class="card-header">
        <h3>答题结果</h3>
        <div class="score-info">
          <span class="score-text">得分：{{ score }}分</span>
          <span class="correct-count">答对：{{ correctCount }}/{{ totalCount }}题</span>
        </div>
      </div>

      <div class="results-container">
        <div
          v-for="(result, index) in questionResults"
          :key="result.questionId"
          class="result-item"
          :class="{ 'correct': result.isCorrect, 'incorrect': !result.isCorrect }"
        >
          <div class="result-header">
            <span class="result-number">第 {{ index + 1 }} 题</span>
            <div class="result-status">
              <span class="result-knowledge">{{ result.knowledge }}</span>
              <span class="result-icon" :class="result.isCorrect ? 'icon-correct' : 'icon-incorrect'">
                {{ result.isCorrect ? '✓' : '✗' }}
              </span>
            </div>
          </div>

          <div class="result-content">
            <div class="question-content" v-html="result.questionContent"></div>

            <div class="answer-comparison">
              <div class="answer-item">
                <span class="answer-label">你的答案：</span>
                <span class="answer-value" :class="{ 'correct': result.isCorrect, 'incorrect': !result.isCorrect }">
                  {{ result.userAnswer }}
                </span>
              </div>
              <div class="answer-item">
                <span class="answer-label">正确答案：</span>
                <span class="answer-value correct">{{ result.correctAnswer }}</span>
              </div>
            </div>

            <!-- 如果答错了，显示解析 -->
            <div class="question-analysis" v-if="!result.isCorrect && result.analysis">
              <div class="analysis-title">解析：</div>
              <div class="analysis-content" v-html="result.analysis"></div>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 错题解析弹窗 -->
    <el-dialog
      title="错题解析"
      :visible.sync="showAnalysisDialog"
      width="70%"
      :before-close="handleCloseAnalysisDialog"
      class="analysis-dialog"
    >
      <div class="analysis-dialog-content">
        <div class="dialog-header">
          <div class="score-summary">
            <span class="final-score">最终得分：{{ score }}分</span>
            <span class="score-detail">答对 {{ correctCount }}/{{ totalCount }} 题</span>
          </div>
        </div>

        <div class="wrong-questions-list">
          <div
            v-for="result in (wrongQuestions || [])"
            :key="result.questionId"
            class="wrong-question-item"
          >
            <div class="question-header">
              <span class="question-number">第 {{ getOriginalQuestionIndex(result.questionId) + 1 }} 题</span>
              <span class="question-knowledge">{{ result.knowledge }}</span>
            </div>

            <div class="question-content" v-html="result.questionContent"></div>

            <div class="answer-details">
              <div class="answer-row">
                <span class="answer-label">你的答案：</span>
                <span class="answer-value incorrect">{{ result.userAnswer }}</span>
              </div>
              <div class="answer-row">
                <span class="answer-label">正确答案：</span>
                <span class="answer-value correct">{{ result.correctAnswer }}</span>
              </div>
            </div>

            <div class="analysis-section" v-if="result.analysis">
              <div class="analysis-title">解析：</div>
              <div class="analysis-content" v-html="result.analysis"></div>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showAnalysisDialog = false">关闭</el-button>
        <el-button type="primary" @click="retryExercise">重新练习</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getQuestion } from '@/api/exercise'
import { getQuestionsByRepoId } from '@/api/question'

export default {
  name: 'AMCexercise',
  data() {
    return {
      exerciseForm: {
        repoId: 9, // 默认选择AMC题库
        knowledgeId: '',
        difficulty: 2, // 默认中等难度
        questionCount: 10 // 默认10题
      },
      rules: {
        repoId: [
          { required: true, message: '请选择题库', trigger: 'change' }
        ],
        difficulty: [
          { required: true, message: '请选择难度系数', trigger: 'change' }
        ],
        questionCount: [
          { required: true, message: '请选择题目数量', trigger: 'change' }
        ]
      },
      knowledgeOptions: [
        { id: '代数', name: '代数' },
        { id: '数论', name: '数论' },
        { id: '几何', name: '几何' },
        { id: '排列组合', name: '排列组合' }
      ],
      loading: false,
      submitting: false,
      exerciseQuestions: [],
      userAnswers: [],
      showResults: false, // 是否显示答题结果
      questionResults: [], // 每道题的答题结果
      showAnalysisDialog: false, // 错题解析弹窗
      wrongQuestions: [] // 错题列表
    }
  },

  created() {
    // this.loadKnowledgeOptions() // 移除动态加载知识点
  },

  computed: {
    score() {
      if (this.questionResults.length === 0) return 0
      const correctCount = this.questionResults.filter(r => r.isCorrect).length
      return Math.round((correctCount / this.questionResults.length) * 100)
    },
    correctCount() {
      return this.questionResults.filter(r => r.isCorrect).length
    },
    totalCount() {
      return this.questionResults.length
    }
  },

  methods: {
    // 开始练习
    async startExercise() {
      try {
        await this.$refs.exerciseForm.validate()

        this.loading = true

        // 使用新的API方法按题库id查找题目
        const response = await getQuestionsByRepoId(this.exerciseForm.repoId)

        console.log('API返回数据:', response) // 添加调试信息
        console.log('当前表单数据:', this.exerciseForm) // 添加表单数据调试

        if (response.code === 1 && response.data && response.data.length > 0) {
          // 暂时不进行过滤，直接显示所有题目
          // 处理每个题目的optionsStr字段，转换为options数组
          const processedQuestions = response.data.map(question => {
            if (question.optionsStr) {
              // 解析optionsStr字段，格式如 "(A)8;(B)11;(C)14;(D)15;(E)18"
              const options = question.optionsStr
                .replace(/[()]/g, '') // 移除括号
                .split(';') // 按分号分割
                .map(option => {
                  const match = option.match(/^([A-Z])(.+)$/)
                  if (match) {
                    return {
                      key: match[1], // A, B, C, D, E
                      value: match[2] // 8, 11, 14, 15, 18
                    }
                  }
                  return null
                })
                .filter(option => option !== null) // 过滤掉无效的选项

              console.log(`题目 ${question.id} 的选项解析:`, {
                original: question.optionsStr,
                parsed: options
              })

              return {
                ...question,
                options: options
              }
            }
            return question
          })

          this.exerciseQuestions = processedQuestions
          this.userAnswers = new Array(processedQuestions.length).fill('')
          this.$message.success(`成功获取 ${processedQuestions.length} 道题目`)
          console.log('显示全部题目:', this.exerciseQuestions)
        } else {
          this.$message.warning('该题库暂无题目')
          console.log('API返回数据异常:', response)
        }
      } catch (error) {
        console.error('获取题目失败:', error)
        this.$message.error('获取题目失败，请重试')
      } finally {
        this.loading = false
      }
    },

    // 随机打乱数组顺序
    shuffleArray(array) {
      const shuffled = [...array]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }
      return shuffled
    },

    // 重置表单
    resetForm() {
      this.$refs.exerciseForm.resetFields()
      this.exerciseForm.repoId = 9
      this.exerciseForm.difficulty = 2
      this.exerciseForm.questionCount = 10
      this.exerciseQuestions = []
      this.userAnswers = []
      this.showResults = false
      this.questionResults = []
      this.showAnalysisDialog = false
      this.wrongQuestions = []
    },

    // 获取难度等级样式类
    getDifficultyClass(difficulty) {
      const classes = {
        1: 'difficulty-easy',
        2: 'difficulty-medium',
        3: 'difficulty-hard'
      }
      return classes[difficulty] || 'difficulty-medium'
    },

    // 获取难度等级文本
    getDifficultyText(difficulty) {
      const texts = {
        1: '简单',
        2: '中等',
        3: '困难'
      }
      return texts[difficulty] || '中等'
    },

    // 提交答案
    async submitAnswers() {
      // 检查是否所有题目都已作答
      const unansweredCount = this.userAnswers.filter(answer => !answer).length
      if (unansweredCount > 0) {
        this.$message.warning(`还有 ${unansweredCount} 道题目未作答`)
        return
      }

      this.submitting = true

      try {
        // 验证每道题的答案并生成结果
        this.questionResults = this.exerciseQuestions.map((question, index) => {
          const userAnswer = this.userAnswers[index]
          const correctAnswer = question.answer
          const isCorrect = userAnswer === correctAnswer

          return {
            questionId: question.id,
            questionContent: question.content,
            userAnswer: userAnswer,
            correctAnswer: correctAnswer,
            isCorrect: isCorrect,
            analysis: question.analysis,
            knowledge: question.knowledge
          }
        })

        // 计算正确题目数量
        const correctCount = this.questionResults.filter(result => result.isCorrect).length
        const totalCount = this.questionResults.length
        const score = Math.round((correctCount / totalCount) * 100)

        // 显示结果
        this.showResults = true

        // 显示总结信息
        if (score === 100) {
          this.$message.success(`恭喜！全部答对！得分：${score}分`)
        } else if (score >= 80) {
          this.$message.success(`很好！答对${correctCount}题，得分：${score}分`)
        } else if (score >= 60) {
          this.$message.warning(`继续加油！答对${correctCount}题，得分：${score}分`)
        } else {
          this.$message.error(`需要加强练习！答对${correctCount}题，得分：${score}分`)
        }

        // 筛选错题
        this.wrongQuestions = this.questionResults.filter(result => !result.isCorrect)
        if (this.wrongQuestions.length > 0) {
          this.showAnalysisDialog = true
        }

        // 这里可以调用提交答案的API
        // const submitData = {
        //   repoId: this.exerciseForm.repoId,
        //   answers: this.userAnswers,
        //   questionIds: this.exerciseQuestions.map(q => q.id),
        //   score: score,
        //   results: this.questionResults
        // }
        // await submitAnswer(submitData)
      } catch (error) {
        console.error('提交答案失败:', error)
        this.$message.error('提交答案失败，请重试')
      } finally {
        this.submitting = false
      }
    },

    // 关闭错题解析弹窗
    handleCloseAnalysisDialog() {
      this.showAnalysisDialog = false
      this.wrongQuestions = []
    },

    // 重新练习
    retryExercise() {
      this.showAnalysisDialog = false
      this.resetForm()
      this.startExercise()
    },

    // 获取错题在原题目列表中的索引
    getOriginalQuestionIndex(questionId) {
      if (!this.exerciseQuestions || !questionId) return 0
      const index = this.exerciseQuestions.findIndex(q => q.id === questionId)
      return index >= 0 ? index : 0
    }
  }
}
</script>
<style lang="scss" scoped>
.amc-exercise {
  padding: 20px;

  .exercise-card {
    margin-bottom: 20px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      h2 {
        margin: 0;
        color: #303133;
      }
    }
  }

  .questions-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        margin: 0;
        color: #303133;
      }

      .question-count {
        color: #909399;
        font-size: 14px;
      }
    }
  }

  .tip-text {
    margin-left: 10px;
    color: #909399;
    font-size: 12px;
  }

  .questions-container {
    .question-item {
      margin-bottom: 30px;
      padding: 20px;
      border: 1px solid #ebeef5;
      border-radius: 8px;
      background-color: #fafafa;

      .question-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;

        .question-number {
          font-weight: bold;
          color: #303133;
          font-size: 16px;
        }

        .question-meta {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .question-knowledge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          color: white;
          background-color: #409eff;
        }

        .question-difficulty {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          color: white;

          &.difficulty-easy {
            background-color: #67c23a;
          }

          &.difficulty-medium {
            background-color: #e6a23c;
          }

          &.difficulty-hard {
            background-color: #f56c6c;
          }

          &.difficulty-unknown {
            background-color: #909399;
          }
        }
      }

      .question-content {
        margin-bottom: 20px;
        line-height: 1.6;
        color: #303133;
      }

      .question-options {
        .option-item {
          display: block;
          margin-bottom: 10px;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background-color 0.3s;

          &:hover {
            background-color: #f5f7fa;
          }
        }
      }

      .question-answer-input {
        margin-top: 15px;

        .el-input {
          width: 200px;
        }

        .answer-tip {
          margin-left: 10px;
          color: #909399;
          font-size: 12px;
        }
      }
    }

    .submit-section {
      text-align: center;
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #ebeef5;
    }
  }

  .results-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        margin: 0;
        color: #303133;
      }

      .score-info {
        display: flex;
        align-items: center;
        gap: 20px;

        .score-text {
          font-size: 18px;
          font-weight: bold;
          color: #409eff;
        }

        .correct-count {
          color: #909399;
          font-size: 14px;
        }
      }
    }
  }

  .results-container {
    .result-item {
      margin-bottom: 30px;
      padding: 20px;
      border: 1px solid #ebeef5;
      border-radius: 8px;
      background-color: #fafafa;
      transition: all 0.3s;

      &.correct {
        border-left: 4px solid #67c23a;
        background-color: #f0f9ff;
      }

      &.incorrect {
        border-left: 4px solid #f56c6c;
        background-color: #fef0f0;
      }

      .result-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;

        .result-number {
          font-weight: bold;
          color: #303133;
          font-size: 16px;
        }

        .result-status {
          display: flex;
          align-items: center;
          gap: 10px;

          .result-knowledge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            color: white;
            background-color: #409eff;
          }

          .result-icon {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;

            &.icon-correct {
              background-color: #67c23a;
              color: white;
            }

            &.icon-incorrect {
              background-color: #f56c6c;
              color: white;
            }
          }
        }
      }

      .result-content {
        .question-content {
          margin-bottom: 20px;
          line-height: 1.6;
          color: #303133;
        }

        .answer-comparison {
          margin-bottom: 20px;

          .answer-item {
            display: flex;
            align-items: center;
            margin-bottom: 10px;

            .answer-label {
              width: 80px;
              color: #606266;
              font-size: 14px;
            }

            .answer-value {
              padding: 4px 12px;
              border-radius: 4px;
              font-weight: bold;
              font-size: 14px;

              &.correct {
                background-color: #f0f9ff;
                color: #67c23a;
                border: 1px solid #b3d8ff;
              }

              &.incorrect {
                background-color: #fef0f0;
                color: #f56c6c;
                border: 1px solid #fbc4c4;
              }
            }
          }
        }

        .question-analysis {
          padding: 15px;
          background-color: #f8f9fa;
          border-radius: 6px;
          border-left: 4px solid #409eff;

          .analysis-title {
            font-weight: bold;
            color: #409eff;
            margin-bottom: 10px;
            font-size: 14px;
          }

          .analysis-content {
            line-height: 1.6;
            color: #606266;
            font-size: 14px;
          }
        }
      }
    }
  }

  .analysis-dialog {
    .analysis-dialog-content {
      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid #ebeef5;

        .score-summary {
          display: flex;
          align-items: baseline;
          gap: 20px;

          .final-score {
            font-size: 24px;
            font-weight: bold;
            color: #409eff;
          }

          .score-detail {
            color: #909399;
            font-size: 16px;
          }
        }
      }

      .wrong-questions-list {
        .wrong-question-item {
          margin-bottom: 30px;
          padding: 20px;
          border: 1px solid #ebeef5;
          border-radius: 8px;
          background-color: #fafafa;

          .question-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;

            .question-number {
              font-weight: bold;
              color: #303133;
              font-size: 16px;
            }

            .question-knowledge {
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              color: white;
              background-color: #f56c6c;
            }
          }

          .question-content {
            margin-bottom: 20px;
            line-height: 1.6;
            color: #303133;
          }

          .answer-details {
            margin-bottom: 20px;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 6px;
            border-left: 4px solid #f56c6c;

            .answer-row {
              display: flex;
              align-items: center;
              margin-bottom: 10px;

              .answer-label {
                width: 80px;
                color: #606266;
                font-size: 14px;
              }

              .answer-value {
                padding: 4px 12px;
                border-radius: 4px;
                font-weight: bold;
                font-size: 14px;

                &.correct {
                  background-color: #f0f9ff;
                  color: #67c23a;
                  border: 1px solid #b3d8ff;
                }

                &.incorrect {
                  background-color: #fef0f0;
                  color: #f56c6c;
                  border: 1px solid #fbc4c4;
                }
              }
            }
          }

          .analysis-section {
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 6px;
            border-left: 4px solid #409eff;

            .analysis-title {
              font-weight: bold;
              color: #409eff;
              margin-bottom: 10px;
              font-size: 14px;
            }

            .analysis-content {
              line-height: 1.6;
              color: #606266;
              font-size: 14px;
            }
          }
        }
      }
    }
  }
}
</style>
